import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Linkedin
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();
  const [settings, setSettings] = useState({});
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings`);
      // API returns an object directly {phone: "...", email: "...", ...}
      setSettings(response.data);
    } catch (error) {
      console.error('Error loading settings:', error);
      // Fallback values
      setSettings({
        phone: '+7 (499) 284-29-88',
        email: 'mail@velox-hint.com',
        address: 'Москва, ул. Поречная, д. 5/14 стр 1',
        work_hours: 'Пн-Пт: 9:00 - 18:00'
      });
    }
  };

  const handleContactClick = () => {
    // If on homepage, just scroll to contact
    if (location.pathname === '/') {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Navigate to homepage and then scroll to contact
      navigate('/#contact');
      // Scroll after navigation
      setTimeout(() => {
        document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const services = [
    "Лицензия Минпромторга",
    "Нотификация ФСБ", 
    "Заключение ГРЧЦ",
    "Сертификат ТР ТС",
    "Заключение ФСТЭК",
    "Декларация соответствия"
  ];

  const quickLinks = [
    { name: t('about'), href: "/about" },
    { name: t('services'), href: "/services" },
    { name: "Процесс работы", href: "/#process" },
    { name: t('contacts'), href: "/#contact" }
  ];

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main footer content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-4 gap-12">
          {/* Company info */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <h3 className="text-2xl font-bold">
                ВЕЛОКС<span className="text-orange-400">ХИНТ</span>
              </h3>
              <p className="text-gray-400 mt-2">
                {t('customsConsulting')}
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Phone className="h-5 w-5 text-orange-400 flex-shrink-0" />
                <span className="text-gray-300">{settings.phone || '+7 (499) 284-29-88'}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-orange-400 flex-shrink-0" />
                <span className="text-gray-300">{settings.email || 'mail@velox-hint.com'}</span>
              </div>
              <div className="flex items-center space-x-3">
                <MapPin className="h-5 w-5 text-orange-400 flex-shrink-0" />
                <span className="text-gray-300">{settings.address || t('centralOffice')}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Clock className="h-5 w-5 text-orange-400 flex-shrink-0" />
                <span className="text-gray-300">{settings.work_hours || t('workTime')}</span>
              </div>
            </div>

            {/* Social links */}
            <div className="flex space-x-4 mt-8">
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white p-2">
                <Linkedin className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-6">{t('ourServices')}</h4>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index}>
                  <a 
                    href="/services" 
                    className="text-gray-400 hover:text-white transition-colors duration-200 text-sm"
                  >
                    {service}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="text-lg font-semibold mb-6">{t('quickLinks')}</h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.href}
                    className="text-gray-400 hover:text-white transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Free Consultation */}
          <div>
            <div className="p-4 bg-gray-800 rounded-lg">
              <h5 className="font-medium mb-2 text-orange-400">
                {t('freeConsultationFooter')}
              </h5>
              <p className="text-xs text-gray-400 mb-3">
                {t('getExpertAdvice')}
              </p>
              <Button 
                size="sm" 
                className="w-full bg-orange-500 hover:bg-orange-600"
                onClick={handleContactClick}
              >
                {t('contactUsFooter')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom footer */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-center items-center">
            <div className="text-sm text-gray-400">
              © {currentYear} ВЕЛОКС ХИНТ. {t('allRightsReserved')}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
